import React from 'react';
import { NavLink } from 'react-router-dom';
import Icon from '../ui/Icon';
import { api } from '../../utils/apiClient';
import styles from './Sidebar.module.css';

// Marketing section badges are real total counts, fetched once on mount (see
// Sidebar.componentDidMount) and merged over these definitions — 'red'
// badgeType is reused for whichever count comes back, same styling as before.
const NAV = [
  {
    section: 'Overview',
    items: [
      { to: '/',          icon: 'dashboard',      label: 'Dashboard' },
      { to: '/analytics', icon: 'bar_chart',       label: 'Analytics' },
    ],
  },
  {
    section: 'Marketing',
    items: [
      { to: '/campaigns', icon: 'campaign',        label: 'Campaigns', countKey: 'campaigns', badgeType: 'red' },
      { to: '/journeys',  icon: 'route',           label: 'Journeys', countKey: 'journeys', badgeType: 'red' },
      { to: '/segments',  icon: 'group',           label: 'Segments', countKey: 'segments', badgeType: 'red' },
      { to: '/abtests',   icon: 'science',         label: 'A/B Tests', badge: 'Soon', badgeType: 'soon', disabled: true },
    ],
  },
  {
    // Real per-channel performance pages (ChannelPerformance.jsx) — matches
    // campaign.validator.js's CHANNELS on the backend, minus 'ivr' (not
    // built yet, same as the campaign wizard's own channel picker).
    section: 'Channels',
    items: [
      { to: '/channels/push',     icon: 'notifications', label: 'Push + In-app' },
      { to: '/channels/whatsapp', icon: 'chat',           label: 'WhatsApp' },
      { to: '/channels/sms',      icon: 'sms',            label: 'SMS' },
      { to: '/channels/email',    icon: 'mail',           label: 'Email' },
      { to: '/channels/rcs',      icon: 'forum',          label: 'RCS' },
    ],
  },
  {
    section: 'Platform',
    items: [
      { to: '/attribution-links', icon: 'link', label: 'Attribution Links', countKey: 'attributionLinks', badgeType: 'red' },
      { to: '/events',   icon: 'bolt',             label: 'Events' },
      { to: '/settings', icon: 'settings',         label: 'Settings' },
    ],
  },
];

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = { counts: {} };
  }

  componentDidMount() {
    Promise.all([
      api.getWithMeta('/campaigns', { limit: 1 }),
      api.getWithMeta('/journeys', { limit: 1 }),
      api.getWithMeta('/segments', { limit: 1 }),
      api.getWithMeta('/attribution/links', { limit: 1 }),
    ]).then(([campaigns, journeys, segments, attributionLinks]) => this.setState({
      counts: {
        campaigns: campaigns.meta?.total,
        journeys: journeys.meta?.total,
        segments: segments.meta?.total,
        attributionLinks: attributionLinks.meta?.total,
      },
    })).catch(() => {});
  }

  render() {
    const { counts } = this.state;
    return (
      <aside className={styles.sidebar}>
        <div className={styles.logoWrap}>
          <div className={styles.logoGem}>
            {/* Trimmed to just the bar-chart+arrow mark from public/logo.svg
                (bounding box of the 4 bars + arrowhead, with padding) — the
                full mark's own white/circle background would double up
                against this container's red background, so that part is
                dropped here; fill comes from the .logoGem svg CSS rule. */}
            <svg viewBox="280 340 435 320">
              <rect x="300" y="540" width="75" height="100" rx="12" />
              <rect x="400" y="500" width="75" height="140" rx="12" />
              <rect x="500" y="460" width="75" height="180" rx="12" />
              <rect x="600" y="420" width="75" height="220" rx="12" />
              <path d="M 637.5 360 L 580 440 L 695 440 Z" />
            </svg>
          </div>
          <span className={styles.logoName}>PMW Click</span>
          <span className={styles.logoVer}>v2.1</span>
        </div>

        <nav className={styles.nav}>
          {NAV.map(group => (
            <div key={group.section} className={styles.navSec}>
              <div className={styles.navSecLbl}>{group.section}</div>
              {group.items.map(item => {
                const badgeValue = item.countKey ? counts[item.countKey] : item.badge;
                return item.disabled ? (
                  <div
                    key={item.to}
                    className={[styles.navItem, styles.navItemDisabled].join(' ')}
                    title="Coming soon"
                  >
                    <span className={styles.navIcon}>
                      <Icon name={item.icon} size={16} />
                    </span>
                    {item.label}
                    {badgeValue != null && (
                      <span className={[styles.nb, styles['nb' + item.badgeType]].join(' ')}>
                        {badgeValue}
                      </span>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      [styles.navItem, isActive ? styles.active : ''].join(' ')
                    }
                  >
                    <span className={styles.navIcon}>
                      <Icon name={item.icon} size={16} />
                    </span>
                    {item.label}
                    {badgeValue != null && (
                      <span className={[styles.nb, styles['nb' + item.badgeType]].join(' ')}>
                        {badgeValue}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    );
  }
}

export default Sidebar;
